import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import {
  ICallPermissions,
  PermissionsValue,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { PERMISSIONS_DEFAULT_TRANSLATIONS } from '@gem/call/constants';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';

import { ICallChangeDefaultPermissionPopupProps } from './CallChangeDefaultPermissionPopup.types';

interface IPermissionValuesOption {
  key: PermissionsValue;
  title: string;
  isActive: boolean;
  onChange(): void;
}

interface ICallChangeDefaultPermissionPopupPresenter {
  permissionValue: PermissionsValue | null;
  ongoingCall: Call;
  defaultPermissions: ICallPermissions | null;
  channelId: string | null;
  permissionValues: IPermissionValuesOption[];
  setValue(value: PermissionsValue | null): void;
  onClose(): void;
  goBack(): void;
}

export const useCallChangeDefaultPermissionPopupPresenter = (
  props: ICommonPopupsProps<ICallChangeDefaultPermissionPopupProps>
): ICallChangeDefaultPermissionPopupPresenter => {
  const { t } = useTranslation();
  const { call, callController, permissionType } = props.params!;

  const presenter = useLocalObservable<ICallChangeDefaultPermissionPopupPresenter>(() => ({
    permissionValue: null,

    onClose(): void {
      props.popupInstance.close();
      props.onClose?.();
    },

    goBack(): void {
      props.popupInstance.close();
      props.onBack?.();
    },

    get ongoingCall(): Call {
      return call;
    },

    get defaultPermissions(): ICallPermissions | null {
      return presenter.ongoingCall?.permissions.defaultPermissions || null;
    },

    get channelId(): string | null {
      return presenter.ongoingCall?.channelId || null;
    },

    setValue(value: PermissionsValue | null): void {
      if (value) {
        presenter.permissionValue = value;
      }
    },

    get permissionValues(): IPermissionValuesOption[] {
      return Object.values(PermissionsValue).map((item) => ({
        key: item,
        title: t(PERMISSIONS_DEFAULT_TRANSLATIONS[item]),
        isActive: presenter.permissionValue ? item === presenter.permissionValue : false,
        onChange: (): void => presenter.setValue(item),
      }));
    },
  }));

  useEffect(() => {
    if (!presenter.ongoingCall || !presenter.ongoingCall?.isStarted) {
      presenter.onClose();
    }
  }, [presenter.ongoingCall?.isStarted]);

  useEffect(() => {
    if (!props.params?.permissionType || !props.params.callController) {
      return;
    }

    presenter.setValue(
      presenter.defaultPermissions ? presenter.defaultPermissions[permissionType] || null : null
    );

    return (): void => {
      if (!presenter.channelId) {
        return;
      }

      if (presenter.ongoingCall && presenter.defaultPermissions) {
        const newPermissions = {
          ...presenter.defaultPermissions,
          [permissionType]: presenter.permissionValue,
        } as Required<ICallPermissions>;

        callController.setCallPermissionsForAll(presenter.channelId, newPermissions);
      }
    };
  }, []);

  return presenter;
};
